import usePageTitle from "../utils/usePageTitle";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Spinner } from "common-tailwind-components";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ClipboardList, FileJson, Square } from "lucide-react";

import {
  get_recording_details,
  analyze_recording,
  cancel_recording_analysis,
  delete_recording,
  delete_recording_analysis_run,
  formatApiRequestError,
  get_processes_list,
  get_users,
  list_recording_analysis_runs,
  rename_recording_analysis_run,
  update_recording_name,
  isApiRequestError,
  set_recording_inclusion,
} from "../api";
import { type ContextualAsk, usePageAsk } from "../chat/AskContext";
import { useAnalysisSubscriptions } from "../utils/useAnalysisSubscriptions";
import { useActionItems } from "../components/dashboard/ActionItemsContext";
import { useLogin } from "../reducers";
import RecordingDeleteDialog from "../components/RecordingDeleteDialog";
import SessionAnalysisReport, {
  SessionAnalysisReportButton,
} from "../components/analytics/SessionAnalysisReport";
import SessionNavigation, { useSessionNeighbours } from "../components/analytics/SessionNavigation";
import { hasLocalActivityScope } from "../components/dashboard/activityScope";
import type {
  AnalyticsData,
  AnalysisProgress,
  AnalyticsStatus,
  ProcessSummary,
  RecordingAnalysisRuns,
  RecordingDetails,
  RerunStatus,
} from "../api/types";
import {
  AnalysisRunSelector,
  AnalyticsHeader,
  RecordingActionControls,
  RecordingTitleControl,
  type RerunOptions,
  buildAnalyticsModel,
  OverviewTab,
  RunCompareBand,
  runLabel,
} from "../components/analytics";
import type { CompareSide } from "../components/processes/processCompareModel";
import type { AnalyticsModel } from "../components/analytics/utils";
import {
  buildSessionContextDetails,
  buildSessionTitle,
} from "../components/analytics/sessionTitle";
import { useTranslation } from "react-i18next";
import useScrollToTop from "../utils/useScrollToTop";
import { buildHash, splitHash } from "../utils/urlHash";
import { canSeeScreenshotsForUser, isAdmin } from "../utils/permissions";

type AnalyticsLocationState = {
  userId?: string;
  returnHash?: string;
};

const normalizeProcessName = (raw: string): string => {
  return raw.trim().toLowerCase().split(/\s+/).join(" ");
};

/** Which process page each of a model's processes links to, by its index. */
const processSlugsFor = (
  model: AnalyticsModel | null,
  lookup: Map<string, string>,
): Record<number, string> => {
  const slugs: Record<number, string> = {};
  model?.processes.forEach((process, index) => {
    const slug = lookup.get(normalizeProcessName(process.trace.name));
    if (slug) {
      slugs[index] = slug;
    }
  });
  return slugs;
};

/** No runs to browse: what the history is before it loads, and if it fails to. */
const NO_RUN_HISTORY: RecordingAnalysisRuns = {
  runs: [],
  default_run: null,
  default_rerun_stage: "lift-to-l1",
};

type UiAnalysisStatus =
  | "loading"
  | "analyzing"
  | "not_synced"
  | "not_analyzed"
  | "recording_too_short"
  | "error"
  | "not_found"
  | "ready";

const Analytics = () => {
  const { t } = useTranslation();
  const { recordingId } = useParams<{ recordingId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, permissions } = useLogin();
  const { refresh: refreshActionItems } = useActionItems();
  const [reportsOpen, setReportsOpen] = useState({ a: location.hash === "#report", b: false });
  useEffect(() => {
    setReportsOpen({ a: location.hash === "#report", b: false });
  }, [recordingId, location.hash]);
  const [sessionPeople, setSessionPeople] = useState<
    Awaited<ReturnType<typeof get_users>>["items"]
  >([]);
  useEffect(() => {
    let cancelled = false;
    get_users()
      .then(result => {
        if (!cancelled) setSessionPeople(result.items);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  const locationState = (location.state as AnalyticsLocationState | null) ?? null;
  useScrollToTop(recordingId);
  const navigateToRecordings = () => {
    // Land on the recordings page while keeping the returning hash's params (filters).
    const search = new URLSearchParams();
    if (new URLSearchParams(location.search).get("sessions") === "mine")
      search.set("sessions", "mine");
    if (hasLocalActivityScope(location)) search.set("scope", "local");
    navigate({
      pathname: "/recordings/",
      search: search.toString(),
      hash: buildHash("", splitHash(locationState?.returnHash ?? "").params),
    });
  };
  const selectedProcessTarget = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const processSlug = params.get("process")?.trim() || null;
    const instanceValue = params.get("instance");
    const instanceNumber =
      instanceValue !== null && /^\d+$/.test(instanceValue) ? Number(instanceValue) : null;

    return {
      processSlug,
      instanceNumber:
        processSlug && instanceNumber !== null && instanceNumber > 0 ? instanceNumber : null,
    };
  }, [location.search]);
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [recording, setRecording] = useState<RecordingDetails | null>(null);
  const neighbours = useSessionNeighbours(
    recording?.id === recordingId ? recording?.user_id : locationState?.userId,
    recordingId,
  );
  const sessionNavigation =
    neighbours?.older || neighbours?.newer ? <SessionNavigation neighbours={neighbours} /> : null;
  const [processSummaries, setProcessSummaries] = useState<ProcessSummary[]>([]);
  const [status, setStatus] = useState<UiAnalysisStatus>("loading");
  const [analysisProgress, setAnalysisProgress] = useState<AnalysisProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const { subscribe, unsubscribe } = useAnalysisSubscriptions();
  const [disableTools, setDisableTools] = useState(false);
  const [analysisCancelBusy, setAnalysisCancelBusy] = useState(false);
  const [runHistory, setRunHistory] = useState<RecordingAnalysisRuns>(NO_RUN_HISTORY);
  const [rerun, setRerun] = useState<RerunStatus | null>(null);
  // The compared run's result, loaded beside the page's own and kept out of the
  // page's state: it is a second thing to read, never the session's status, its
  // re-run pill, or what a progress subscription reports on.
  const [compareData, setCompareData] = useState<AnalyticsData | null>(null);
  const [compareError, setCompareError] = useState<string | null>(null);
  const [rerunBusy, setRerunBusy] = useState(false);
  const isRecordingOwner = (recording?.user_id ?? locationState?.userId) === user.id;
  const canShowScreenshots =
    !!recording && canSeeScreenshotsForUser(permissions, user, recording.user_id);
  const canDeleteRecording = isRecordingOwner;
  const canArchiveRecording = !!recording && isRecordingOwner;
  const canAdminEditRecordingAnalysis = isAdmin(permissions) && !!recordingId;
  // The run history is an operator's tool; everyone else sees the newest
  // completed run and nothing about the others.
  const canBrowseRuns = isAdmin(permissions) && !!recordingId;
  const selectedRunId = canBrowseRuns
    ? new URLSearchParams(location.search).get("run")?.trim() || null
    : null;
  /**
   * The run stacked under the selected one, or `null` when not comparing. Read
   * under the same gate as `run`: the comparison is a view of two analysis
   * runs, so it is the run history's audience or nobody.
   */
  const compareRunId = canBrowseRuns
    ? new URLSearchParams(location.search).get("compare")?.trim() || null
    : null;
  // Whether a result is on screen, read from callbacks that outlive the render
  // that created them: it decides whether an analysis is this page's own state
  // or a re-run happening beside it.
  const hasResultRef = React.useRef(false);

  useEffect(() => {
    let cancelled = false;

    get_processes_list()
      .then(processes => {
        if (!cancelled) {
          setProcessSummaries(processes);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProcessSummaries([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleAnalysisStatus = (nextStatus: AnalyticsStatus) => {
    if (nextStatus.status !== "analyzing") {
      return;
    }
    // A re-analysis runs beside the result already on screen: it moves the
    // re-run pill, never the page's own status, so the session keeps showing
    // what it showed before.
    if (hasResultRef.current) {
      setRerun({ status: "running", progress: nextStatus.progress });
      return;
    }
    setStatus("analyzing");
    setAnalysisProgress(nextStatus.progress);
  };

  /** The recording's run history, for the users allowed to see it. */
  const loadRuns = useCallback(async () => {
    if (!recordingId || !canBrowseRuns) {
      return;
    }
    try {
      setRunHistory(await list_recording_analysis_runs(recordingId));
    } catch {
      // The history is an extra on a page that works without it, so a failure
      // to load it leaves the analysis alone.
      setRunHistory(NO_RUN_HISTORY);
    }
  }, [recordingId, canBrowseRuns]);

  /**
   * Reflect a details response into the UI state, subscribing for updates if
   * the analysis is still running.
   */
  const applyDetails = (details: RecordingDetails) => {
    setRecording(details);
    setAnalysisProgress(null);
    setRerun(details.rerun ?? null);
    const s = details.status;
    hasResultRef.current = s.status === "completed";
    switch (s.status) {
      case "completed":
        setData(s.result);
        setStatus("ready");
        void loadRuns();
        // A re-analysis running behind this result reports over the same
        // subscription: without it the re-run pill — and the progress behind
        // it — would sit at whatever this response happened to carry.
        if (details.rerun?.status === "running") {
          subscribeForDetails(details.id);
        }
        break;
      case "failed":
        setError(s.error_message || t("analysis_failed"));
        setStatus("error");
        break;
      case "analyzing":
        setAnalysisProgress(s.progress);
        subscribeForDetails(details.id);
      // fallthrough
      case "not_analyzed":
      case "not_synced":
      case "recording_too_short":
        setStatus(s.status);
        break;
    }
  };

  /** Reload the recording on the run the URL names, and show it. */
  const reloadDetails = async (id: string) => {
    applyDetails(await get_recording_details(id, selectedRunId ?? undefined));
  };

  const subscribeForDetails = (recordingId: string) => {
    subscribe(
      recordingId,
      applyDetails,
      (errorMessage, reason) => {
        setAnalysisProgress(null);
        // A re-analysis that failed leaves the recording exactly as it was —
        // only the re-run pill says otherwise.
        if (hasResultRef.current) {
          setRerun({ status: "failed", error_message: errorMessage });
          void loadRuns();
          return;
        }
        if (reason === "not_analyzed") {
          setStatus("not_analyzed");
          return;
        }
        if (reason === "not_synced") {
          setStatus("not_synced");
          return;
        }
        if (reason === "recording_too_short") {
          setStatus("recording_too_short");
          return;
        }
        setError(errorMessage);
        setStatus("error");
      },
      handleAnalysisStatus,
    );
  };

  useEffect(() => {
    if (!recordingId) {
      setError(t("missing_analytics_identifier_in_url"));
      setStatus("error");
      return;
    }

    setStatus("loading");
    setError(null);
    setData(null);
    setRecording(null);
    setAnalysisProgress(null);
    setArchiveError(null);
    hasResultRef.current = false;

    let cancelled = false;
    // A quick session hop must not let the previous request replace the new session.
    get_recording_details(recordingId, selectedRunId ?? undefined)
      .then(details => {
        if (!cancelled) applyDetails(details);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(formatApiRequestError(err, t));
        if (isApiRequestError(err) && err.status === 404) {
          setStatus("not_found");
          return;
        }
        setStatus("error");
      });

    return () => {
      cancelled = true;
      unsubscribe(recordingId);
    };
  }, [recordingId, selectedRunId, unsubscribe]);

  /**
   * Load the compared run's analysis. A read on its own: only completed runs
   * can be picked for a comparison, so anything but a result here is a load
   * failure rather than a state the band has to render.
   */
  useEffect(() => {
    setCompareData(null);
    setCompareError(null);
    if (!recordingId || !compareRunId) {
      return;
    }

    let cancelled = false;
    get_recording_details(recordingId, compareRunId)
      .then(details => {
        if (cancelled) {
          return;
        }
        if (details.status.status === "completed") {
          setCompareData(details.status.result);
        } else {
          setCompareError(t("compare_run_load_failed"));
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setCompareError(formatApiRequestError(err, t));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [recordingId, compareRunId, t]);

  const handleAnalyze = useCallback(async () => {
    if (!recordingId) return;
    setStatus("analyzing");
    setError(null);
    setAnalysisProgress(null);
    try {
      const initialStatus = await analyze_recording(
        recordingId,
        undefined,
        undefined,
        disableTools,
      );
      handleAnalysisStatus(initialStatus);
    } catch (err) {
      setAnalysisProgress(null);
      setError(err instanceof Error ? err.message : t("analysis_failed"));
      setStatus("error");
      return;
    }
    refreshActionItems();
    subscribeForDetails(recordingId);
  }, [disableTools, recordingId, refreshActionItems, t]);

  const handleCancelAnalysis = useCallback(async () => {
    if (!recordingId) return;
    setAnalysisCancelBusy(true);
    setError(null);
    try {
      const nextStatus = await cancel_recording_analysis(recordingId);
      unsubscribe(recordingId);
      setAnalysisProgress(null);

      if (nextStatus.status === "completed") {
        try {
          await reloadDetails(recordingId);
        } catch (err) {
          setError(err instanceof Error ? err.message : t("unable_to_load_recording_data"));
          setStatus("error");
        }
      } else if (nextStatus.status === "failed") {
        setError(nextStatus.error_message || t("analysis_failed"));
        setStatus("error");
      } else if (nextStatus.status === "not_synced") {
        setStatus("not_synced");
      } else if (nextStatus.status === "not_analyzed") {
        setStatus("not_analyzed");
      } else {
        handleAnalysisStatus(nextStatus);
        subscribeForDetails(recordingId);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t("failed_to_stop_analysis"));
    } finally {
      setAnalysisCancelBusy(false);
    }
  }, [recordingId, unsubscribe, t]);

  /**
   * Stop the re-analysis running behind the displayed result. Cancelling stores
   * nothing, so the recording stays on the run it was already showing.
   */
  const handleCancelRerun = useCallback(async () => {
    if (!recordingId) return;
    await cancel_recording_analysis(recordingId);
    unsubscribe(recordingId);
    setRerun(null);
    await reloadDetails(recordingId);
    await loadRuns();
  }, [recordingId, selectedRunId, unsubscribe, loadRuns]);

  /**
   * Put the runs on screen in the URL, so they survive tab switches and
   * sharing. A key left out is left as it was; `null` clears it.
   */
  const setRunParams = useCallback(
    (next: { run?: string | null; compare?: string | null }) => {
      const params = new URLSearchParams(location.search);
      const apply = (key: string, value: string | null | undefined) => {
        if (value === undefined) {
          return;
        }
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
      };
      apply("run", next.run);
      apply("compare", next.compare);
      const search = params.toString();
      navigate(
        {
          pathname: location.pathname,
          search: search ? `?${search}` : "",
          hash: location.hash,
        },
        { state: location.state },
      );
    },
    [location.hash, location.pathname, location.search, location.state, navigate],
  );

  /** Show a run. Picking the compared one ends the comparison, rather than
   *  stacking a run under itself. */
  const handleSelectRun = useCallback(
    (runId: string | null) => {
      const shown = runId ?? runHistory.default_run ?? null;
      setRunParams({
        run: runId,
        compare: shown === compareRunId ? null : undefined,
      });
    },
    [compareRunId, runHistory.default_run, setRunParams],
  );

  /** Stack a run under the shown one, or end the comparison with `null`. */
  const handleCompareRun = useCallback(
    (runId: string | null) => setRunParams({ compare: runId }),
    [setRunParams],
  );

  /**
   * Compare two runs picked together, the first as the top band. Both params
   * move in one navigation: picking the pair is one act, and stepping through
   * it as two would leave the page briefly showing a run against itself.
   */
  const handleComparePair = useCallback(
    (aRunId: string, bRunId: string) =>
      setRunParams({
        // The default run is shown with no `run` in the URL at all.
        run: aRunId === runHistory.default_run ? null : aRunId,
        compare: bRunId,
      }),
    [runHistory.default_run, setRunParams],
  );

  const handleRerun = useCallback(
    async (options?: RerunOptions) => {
      if (!recordingId) return;
      setRerunBusy(true);
      try {
        const initialStatus = await analyze_recording(
          recordingId,
          options?.startStage,
          options?.baseRunId,
          options?.disableTools,
        );
        handleAnalysisStatus(initialStatus);
        subscribeForDetails(recordingId);
      } catch (err) {
        // Re-thrown for the dialog that asked, which keeps the failure on
        // screen; the pill covers a re-run started from the menu instead.
        setRerun({
          status: "failed",
          error_message: formatApiRequestError(err, t),
        });
        throw err;
      } finally {
        setRerunBusy(false);
      }
    },
    [recordingId, t],
  );

  const handleRenameRun = useCallback(
    async (runId: string, name: string | null) => {
      if (!recordingId) return;
      await rename_recording_analysis_run(recordingId, runId, name);
      await loadRuns();
    },
    [recordingId, loadRuns],
  );

  const handleDeleteRun = useCallback(
    async (runId: string) => {
      if (!recordingId) return;
      const shownRunId = selectedRunId ?? runHistory.default_run ?? null;
      const history = await delete_recording_analysis_run(recordingId, runId);
      setRunHistory(history);
      // Deleting one of the two runs being compared leaves a comparison with
      // one side: the survivor becomes the run the session shows, on its own.
      if (compareRunId && (runId === compareRunId || runId === shownRunId)) {
        const survivor = runId === compareRunId ? selectedRunId : compareRunId;
        setRunParams({
          // The survivor is named outright unless it is the default, which the
          // page shows with no `run` at all.
          run: survivor === history.default_run ? null : survivor,
          compare: null,
        });
        return;
      }
      // Whatever the page was showing may be the run that just went, and the
      // default may have moved: reload from the run the URL still names, or
      // drop back to the default when that run is gone.
      if (selectedRunId && !history.runs.some(run => run.id === selectedRunId)) {
        handleSelectRun(null);
        return;
      }
      await reloadDetails(recordingId);
    },
    [
      recordingId,
      selectedRunId,
      compareRunId,
      runHistory.default_run,
      handleSelectRun,
      setRunParams,
    ],
  );

  const handleRenameRecording = useCallback(async (id: string, name: string | null) => {
    const nextRecording = await update_recording_name(id, name);
    setRecording(prev => (prev ? { ...prev, name: nextRecording.name } : prev));
  }, []);

  const handleDeleteRecording = async () => {
    if (!recordingId) {
      return;
    }
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await delete_recording(recordingId);
      setDeleteDialogOpen(false);
      navigateToRecordings();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : t("recording_delete_failed"));
    } finally {
      setDeleteBusy(false);
    }
  };

  const requestDeleteRecording = () => {
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  const handleArchiveChange = async (id: string, archived: boolean) => {
    setArchiveBusy(true);
    setArchiveError(null);
    try {
      const updatedRecording = await set_recording_inclusion(id, !archived);
      setRecording(prev =>
        prev
          ? {
              ...prev,
              include_in_processes: updatedRecording.include_in_processes,
            }
          : prev,
      );
    } catch (err) {
      setArchiveError(err instanceof Error ? err.message : t("recording_archive_update_failed"));
    } finally {
      setArchiveBusy(false);
    }
  };

  const deleteDialog = (
    <RecordingDeleteDialog
      open={deleteDialogOpen}
      busy={deleteBusy}
      error={deleteError}
      location="synced"
      onCancel={() => setDeleteDialogOpen(false)}
      onConfirm={handleDeleteRecording}
    />
  );

  const model = useMemo(() => {
    if (!data) {
      return null;
    }
    return buildAnalyticsModel(data);
  }, [data]);

  const compareModel = useMemo(() => {
    if (!compareData) {
      return null;
    }
    return buildAnalyticsModel(compareData);
  }, [compareData]);

  const processSlugLookup = useMemo(() => {
    const lookup = new Map<string, string>();

    processSummaries.forEach(process => {
      lookup.set(normalizeProcessName(process.name), process.slug);
      process.aliases.forEach(alias => {
        lookup.set(normalizeProcessName(alias), process.slug);
      });
    });

    return lookup;
  }, [processSummaries]);

  const processSlugs = useMemo(
    () => processSlugsFor(model, processSlugLookup),
    [model, processSlugLookup],
  );

  const compareProcessSlugs = useMemo(
    () => processSlugsFor(compareModel, processSlugLookup),
    [compareModel, processSlugLookup],
  );

  const analysisPercent = analysisProgress
    ? Math.min(
        100,
        Math.max(
          0,
          Math.round((analysisProgress.stage / Math.max(analysisProgress.stageTotal, 1)) * 100),
        ),
      )
    : 0;
  const recordingTitle = useMemo(
    () =>
      recordingId
        ? buildSessionTitle(data ?? undefined, recordingId, recording ?? undefined)
        : t("recordings"),
    [data, recording, recordingId, t],
  );
  usePageTitle(
    recording && recording.id === recordingId
      ? recordingTitle
      : t(status === "not_found" ? "recording_not_found_title" : "session_details"),
  );
  const sessionLabel = `${t("chat_this_session")}: ${recordingTitle}`;
  const sessionDetails = buildSessionContextDetails(
    data,
    recording,
    model ? model.totalActive * 60 : undefined,
  );
  const sessionAsk: ContextualAsk | null = recordingId
    ? {
        sessionKey: `recording:${recordingId}`,
        page: {
          kind: "session",
          name: recordingTitle,
        },
        filters: { recordingIds: [recordingId] },
        sessionProcesses: [
          ...new Set(
            model?.processes
              .filter(process => process.trace.significance && process.trace.name.trim())
              .map(process => process.trace.name) ?? [],
          ),
        ],
        label: `${sessionLabel} (${sessionDetails})`,
        sessionLabel,
        sessionDetails,
        suggestions: [
          t("chat_suggestion_recording_time"),
          t("chat_suggestion_recording_summary"),
          t("chat_suggestion_recording_unstructured"),
        ],
      }
    : null;
  usePageAsk(sessionAsk);
  const recordingTitleControl = (
    <RecordingTitleControl
      className="min-w-0 flex-1"
      recording={recording}
      title={recordingTitle}
      onRename={isRecordingOwner ? handleRenameRecording : undefined}
      variant="detail"
    />
  );
  const recordingActionProps = {
    recording: canArchiveRecording ? recording : null,
    archiveBusy,
    onArchiveChange: isRecordingOwner ? handleArchiveChange : undefined,
    loading: status === "loading",
  };
  const recordingActions = (onDelete?: () => void) => {
    if (!canArchiveRecording && !onDelete && !canAdminEditRecordingAnalysis && !sessionNavigation) {
      return null;
    }

    return (
      <div className="flex shrink-0 items-center gap-2">
        {canAdminEditRecordingAnalysis && (
          <button
            type="button"
            onClick={() =>
              navigate(`/administration/recording-import/${encodeURIComponent(recordingId)}/`)
            }
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-outline bg-surface text-text-dim transition hover:border-info/40 hover:bg-info/10 hover:text-info-strong"
            title={t("edit_recording_analysis")}
            aria-label={t("edit_recording_analysis")}
          >
            <FileJson className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
        {sessionNavigation}
        <RecordingActionControls {...recordingActionProps} onDelete={onDelete} />
      </div>
    );
  };
  const recordingHeader = (onDelete?: () => void) => (
    <div className="flex items-start justify-between gap-4">
      {recordingTitleControl}
      {recordingActions(onDelete)}
    </div>
  );

  if (status === "loading" || status === "analyzing") {
    return (
      <>
        <div className="dashboard-page @container min-w-0">
          <div className="flex flex-col items-start justify-between gap-4 @[800px]:flex-row">
            <div className="text-text-mid flex min-w-0 flex-1 items-start gap-3">
              <Spinner size={18} className="mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                {recording && <div className="mb-1.5">{recordingTitleControl}</div>}
                <div>
                  {status === "loading" ? t("loading_recording") : t("analyzing_recording")}
                </div>
                {status === "analyzing" && analysisProgress && (
                  <div className="mt-3 w-full max-w-xl">
                    <div className="flex items-center justify-between gap-3 text-xs text-text-dim">
                      <span className="truncate">
                        Stage {analysisProgress.stage} of {analysisProgress.stageTotal}
                      </span>
                      <span className="shrink-0">{analysisPercent}%</span>
                    </div>
                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted-surface">
                      <div
                        className="h-full rounded-full bg-info transition-all"
                        style={{ width: `${analysisPercent}%` }}
                      />
                    </div>
                    <p className="mt-2 truncate text-sm text-text-mid">
                      {analysisProgress.message}
                    </p>
                  </div>
                )}
                {status === "analyzing" && error && (
                  <p className="mt-3 text-sm text-danger-strong">{error}</p>
                )}
                {archiveError && <p className="mt-3 text-sm text-danger-strong">{archiveError}</p>}
                {status === "analyzing" && (
                  <button
                    type="button"
                    onClick={handleCancelAnalysis}
                    disabled={analysisCancelBusy}
                    className="mt-4 inline-flex h-9 items-center gap-2 rounded-lg border border-danger/25 bg-transparent px-3 text-sm font-medium text-danger transition hover:bg-danger/10 hover:text-danger-strong disabled:cursor-not-allowed disabled:opacity-60"
                    title={t("stop_analysis")}
                  >
                    <Square size={14} aria-hidden="true" />
                    {analysisCancelBusy ? t("stopping_analysis") : t("stop_analysis")}
                  </button>
                )}
              </div>
            </div>
            {recordingActions(canDeleteRecording ? requestDeleteRecording : undefined)}
          </div>
        </div>
        {deleteDialog}
      </>
    );
  }

  if (
    status === "not_found" ||
    status === "not_synced" ||
    status === "not_analyzed" ||
    status === "recording_too_short" ||
    status === "error"
  ) {
    const analyzeDisabled = status === "recording_too_short";
    return (
      <>
        <div className="dashboard-page @container min-w-0">
          {status === "not_found" ? (
            <div className="max-w-2xl rounded-xl border border-outline bg-surface p-6 shadow-sm">
              <h1 className="text-xl font-semibold text-text">{t("recording_not_found_title")}</h1>
              <p className="mt-3 text-text-dim">{t("recording_not_found_message")}</p>
              {error && <p className="mt-3 text-sm text-danger-strong">{error}</p>}
              <button
                type="button"
                onClick={navigateToRecordings}
                className="mt-5 rounded-lg border border-outline bg-surface px-4 py-2 text-sm font-medium text-text shadow-sm transition hover:bg-muted-surface"
              >
                {t("back_to_recordings")}
              </button>
            </div>
          ) : (
            <>
              {recordingHeader(canDeleteRecording ? requestDeleteRecording : undefined)}
              {archiveError && <p className="mt-3 text-danger-strong">{archiveError}</p>}
              {error && <p className="mt-3 text-danger-strong">{error}</p>}
              <p className="mt-3 text-text-dim">
                {status === "error"
                  ? t("something_went_wrong_you_can_retry_the_analysis")
                  : status === "not_synced"
                    ? t("uploading_recording_data")
                    : status === "recording_too_short"
                      ? t("recording_too_short_page_message")
                      : t("this_recording_has_not_been_analyzed_yet")}
              </p>
              {status !== "not_synced" && (
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={handleAnalyze}
                    disabled={analyzeDisabled}
                    className={`rounded-lg border border-outline bg-surface px-4 py-2 text-sm font-medium text-text shadow-sm transition ${
                      analyzeDisabled ? "cursor-not-allowed opacity-60" : "hover:bg-muted-surface"
                    }`}
                  >
                    {status === "error" ? t("retry_analysis") : t("analyze")}
                  </button>
                  {!analyzeDisabled && isAdmin(permissions) && (
                    <div className="mt-4 flex max-w-xl items-center justify-between gap-3 rounded-xl border border-outline bg-muted-surface/35 p-3 shadow-sm">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={!disableTools}
                        onClick={() => setDisableTools(disabled => !disabled)}
                        className="inline-flex items-center gap-2 rounded-full border border-outline bg-surface px-2.5 py-1 text-xs font-medium text-text-mid transition hover:bg-muted-surface"
                      >
                        <span>{t("use_lift_tools")}</span>
                        <span
                          className={`inline-flex h-3.5 w-6 items-center rounded-full transition ${
                            disableTools ? "bg-outline" : "bg-primary"
                          }`}
                        >
                          <span
                            className={`h-2.5 w-2.5 rounded-full bg-surface shadow transition ${
                              disableTools ? "translate-x-0.5" : "translate-x-3"
                            }`}
                          />
                        </span>
                      </button>
                      <span className="rounded-full border border-warning/30 bg-warning/10 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-warning-strong">
                        Admin
                      </span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        {deleteDialog}
      </>
    );
  }

  if (!data || !model) {
    return (
      <>
        <div className="dashboard-page @container min-w-0">
          {recordingHeader(canDeleteRecording ? requestDeleteRecording : undefined)}
          {archiveError && <p className="mt-3 text-danger-strong">{archiveError}</p>}
          <p className="mt-3 text-danger-strong">{t("no_recording_data_available")}</p>
        </div>
        {deleteDialog}
      </>
    );
  }

  const comparing = !!compareRunId;
  const shownRunId = selectedRunId ?? runHistory.default_run ?? null;
  /** What the run picker calls a run, for the bar over that run's band. */
  const bandLabel = (runId: string | null) => {
    const run = runId ? runHistory.runs.find(candidate => candidate.id === runId) : undefined;
    return run ? runLabel(run) : t("analysis_runs");
  };

  /**
   * The run picker, for the page header (no `side`) or for one comparison
   * band's own bar. A band's re-aims that band alone, which is why the header's
   * gives way to them for as long as the comparison is on.
   */
  const runPicker = (side?: CompareSide) =>
    canBrowseRuns ? (
      <AnalysisRunSelector
        side={side}
        runs={runHistory.runs}
        defaultRunId={runHistory.default_run ?? null}
        selectedRunId={selectedRunId}
        compareRunId={compareRunId}
        onSelect={handleSelectRun}
        onCompare={handleCompareRun}
        onComparePair={handleComparePair}
        defaultRerunStage={runHistory.default_rerun_stage}
        onRerun={handleRerun}
        onRename={handleRenameRun}
        onDelete={handleDeleteRun}
        rerunBusy={rerunBusy}
      />
    ) : null;

  const runControls = (side?: CompareSide) => (
    <div className="flex flex-wrap items-center gap-2">
      {runPicker(side)}
      {isAdmin(permissions) && (side || !comparing) && (
        <SessionAnalysisReportButton
          open={reportsOpen[side ?? "a"]}
          reportId={`session-analysis-report-${side ?? "a"}`}
          onToggle={() =>
            setReportsOpen(current => ({
              ...current,
              [side ?? "a"]: !current[side ?? "a"],
            }))
          }
        />
      )}
    </div>
  );

  /**
   * One run's session content. `deepLink` is the band that answers the `?process=`
   * the URL may carry: expanding and scrolling to a process is one thing the
   * viewport can do, so the second band is left where the reader put it — its
   * own timeline and evidence controls still act on it alone, through its own refs.
   */
  const sessionBody = (
    bandModel: AnalyticsModel,
    runId: string | null,
    slugs: Record<number, string>,
    deepLink: boolean,
  ) => (
    <>
      {recordingId && (
        <OverviewTab
          model={bandModel}
          analysisReport={
            isAdmin(permissions) && (
              <SessionAnalysisReport
                open={reportsOpen[deepLink ? "a" : "b"]}
                onClose={() =>
                  setReportsOpen(current => ({
                    ...current,
                    [deepLink ? "a" : "b"]: false,
                  }))
                }
                reportId={`session-analysis-report-${deepLink ? "a" : "b"}`}
                expandFromLink={deepLink && location.hash === "#report"}
                recordingId={recordingId}
                runId={runId ?? undefined}
                knownRunIds={runHistory.runs.map(run => run.id)}
              />
            )
          }
          recordingId={recordingId}
          canShowScreenshots={canShowScreenshots}
          processSlugs={slugs}
          onTargetChange={
            deepLink
              ? (slug, instance) => {
                  const params = new URLSearchParams(location.search);
                  if (slug) params.set("process", slug);
                  else params.delete("process");
                  if (slug && instance !== null) params.set("instance", String(instance));
                  else params.delete("instance");
                  if (params.toString() !== new URLSearchParams(location.search).toString())
                    navigate(
                      {
                        pathname: location.pathname,
                        search: params.toString(),
                        hash: location.hash,
                      },
                      { replace: true, state: location.state },
                    );
                }
              : undefined
          }
          selectedProcessSlug={deepLink ? selectedProcessTarget.processSlug : null}
          selectedInstanceNumber={deepLink ? selectedProcessTarget.instanceNumber : null}
          scrollToTimeline={
            deepLink && location.hash === "#timeline" && !selectedProcessTarget.processSlug
          }
        />
      )}
    </>
  );

  return (
    <>
      <div className="dashboard-page @container min-w-0">
        <div className="flex flex-col gap-4 lg:flex-row lg:gap-0">
          <div className="min-w-0 flex-1">
            <AnalyticsHeader
              data={data}
              title={recordingTitle}
              mappedSeconds={model.totalActive * 60}
              personName={(() => {
                const person =
                  recording?.user_id === user.id
                    ? user
                    : sessionPeople.find(person => person.id === recording?.user_id);
                return person
                  ? `${person.first_name ?? ""} ${person.last_name ?? ""}`.trim() || person.username
                  : undefined;
              })()}
              recording={recording ?? undefined}
              archiveBusy={archiveBusy}
              onArchiveChange={canArchiveRecording ? handleArchiveChange : undefined}
              onRequestDelete={canDeleteRecording ? requestDeleteRecording : undefined}
              rerun={rerun}
              onCancelRerun={canBrowseRuns ? handleCancelRerun : undefined}
              runSelector={runControls()}
              navigation={sessionNavigation}
            />
            {archiveError && (
              <p className="-mt-2 mb-4 text-sm text-danger-strong">{archiveError}</p>
            )}
            {comparing ? (
              // Stacked, not side by side: the sections are full-width reads (the
              // report especially), so the second run goes under the first and
              // the comparison is made by scrolling.
              <div className="flex flex-col gap-10">
                <RunCompareBand
                  side="a"
                  label={bandLabel(shownRunId)}
                  latest={!!shownRunId && shownRunId === runHistory.default_run}
                  picker={runControls("a")}
                >
                  {sessionBody(model, selectedRunId, processSlugs, true)}
                </RunCompareBand>
                <RunCompareBand
                  side="b"
                  label={bandLabel(compareRunId)}
                  latest={compareRunId === runHistory.default_run}
                  picker={runControls("b")}
                >
                  {compareError ? (
                    <p className="text-sm text-danger-strong">{compareError}</p>
                  ) : compareModel ? (
                    sessionBody(compareModel, compareRunId, compareProcessSlugs, false)
                  ) : (
                    <div className="flex items-center gap-3 text-text-mid">
                      <Spinner size={18} className="shrink-0" />
                      {t("loading_recording")}
                    </div>
                  )}
                </RunCompareBand>
              </div>
            ) : (
              sessionBody(model, selectedRunId, processSlugs, true)
            )}
          </div>
        </div>
      </div>
      {deleteDialog}
    </>
  );
};

export default Analytics;
